import { useFetcher } from '@remix-run/react'
import { cellToParent, gridDisk } from 'h3-js'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { FETCH_RESOLUTION, TWO_MINUTES_MS } from '#app/consts'
import { type PlotsActionData } from '#app/routes/resources+/plot.get-by-ids'
import { type LargeClusterActionData } from '#app/routes/resources+/plot.get-large-clusters'
import { useStoreContext } from '#app/store'

export const useFetchPlots = () => {
	const plotFetcher = useFetcher<PlotsActionData>({ key: 'plots' })
	const fetchedSmallClusterPlotIds = plotFetcher.data?.smallClusterPlotIds
	const fetchedMediumClusterPlotIds = plotFetcher.data?.mediumClusterPlotIds

	const fetchedPlotIds = useStoreContext(s => s.fetchedPlotIds)

	const setFetchedPlotIds = useStoreContext(s => s.setFetchedPlotIds)
	const addLoadedPlots = useStoreContext(s => s.addLoadedPlots)
	const addSmallClusterPlotIds = useStoreContext(s => s.addSmallClusterPlotIds)
	const addMediumClusterPlotIds = useStoreContext(
		s => s.addMediumClusterPlotIds,
	)
	const setLargeClusterPlotIds = useStoreContext(s => s.setLargeClusterPlotIds)
	const setLargestClusterPlotIds = useStoreContext(
		s => s.setLargestClusterPlotIds,
	)

	const largeClusterFetcher = useFetcher<LargeClusterActionData>({
		key: 'large-cluster-plots',
	})
	const fetchedLargeClusterPlotIds =
		largeClusterFetcher.data?.largeClusterPlotIds
	const fetchedLargestClusterPlotIds =
		largeClusterFetcher.data?.largestClusterPlotIds

	const globalRequestStarted = useRef(false)
	const pendingCenter = useRef<string | null>(null)
	const requestInFlight = useRef(false)
	const fetchedAtById = useMemo(
		() => new Map(fetchedPlotIds.map(item => [item.plotId, item.fetchedAt])),
		[fetchedPlotIds],
	)
	const lastPlotsStateRef = useRef(plotFetcher.state)
	const lastRequestedPlotIdsRef = useRef<string[]>([])

	const fetchPlots = useCallback(
		async (ids: string[]) => {
			const formData = new FormData()

			requestInFlight.current = true
			lastRequestedPlotIdsRef.current = ids

			formData.append('plotIds', JSON.stringify(ids))
			plotFetcher.submit(formData, {
				method: 'POST',
				action: '/resources/plot/get-by-ids',
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)

	const fetchLargeClusters = useCallback(async () => {
		if (fetchedLargeClusterPlotIds || largeClusterFetcher.state !== 'idle')
			return

		console.log('Fetching all clusters globally...')
		const formData = new FormData()

		largeClusterFetcher.submit(formData, {
			method: 'POST',
			action: '/resources/plot/get-large-clusters',
		})
	}, [fetchedLargeClusterPlotIds, largeClusterFetcher])

	// Fetch all clusters globally on map initialization
	useEffect(() => {
		if (globalRequestStarted.current) return
		globalRequestStarted.current = true
		void fetchLargeClusters()
	}, [fetchLargeClusters])

	// Retry fetching global clusters if request fails or gets aborted (e.g. during deployments)
	const lastLargeClusterStateRef = useRef(largeClusterFetcher.state)
	useEffect(() => {
		const currentState = largeClusterFetcher.state
		const lastState = lastLargeClusterStateRef.current
		lastLargeClusterStateRef.current = currentState

		if (
			lastState !== 'idle' &&
			currentState === 'idle' &&
			!(largeClusterFetcher.data as any)?.largeClusterPlotIds
		) {
			console.warn(
				'Global clusters fetch failed or aborted. Retrying in 5 seconds...',
			)
			const timer = setTimeout(() => {
				fetchLargeClusters()
			}, 5000)
			return () => clearTimeout(timer)
		}
	}, [largeClusterFetcher.state, largeClusterFetcher.data, fetchLargeClusters])

	// Retry fetching visible plots if request fails or gets aborted (e.g. during deployments)
	useEffect(() => {
		const currentState = plotFetcher.state
		const lastState = lastPlotsStateRef.current
		lastPlotsStateRef.current = currentState

		if (
			lastState !== 'idle' &&
			currentState === 'idle' &&
			!(plotFetcher.data as any)?.requestedPlotIds &&
			lastRequestedPlotIdsRef.current.length > 0
		) {
			console.warn('Places fetch failed or aborted. Retrying in 3 seconds...')
			const timer = setTimeout(() => {
				fetchPlots(lastRequestedPlotIdsRef.current)
			}, 3000)
			return () => clearTimeout(timer)
		}
	}, [plotFetcher.state, plotFetcher.data, fetchPlots])

	const reconcileVisiblePlotIds = useCallback(
		async (centerPlot: string) => {
			if (requestInFlight.current || plotFetcher.state !== 'idle') {
				pendingCenter.current = centerPlot
				return
			}
			const parentPlotId = cellToParent(centerPlot, FETCH_RESOLUTION)
			const plotIds = gridDisk(parentPlotId, 2)

			// filter out any recently fetched Plots
			const now = Date.now()
			const newPlotIds = plotIds.filter(
				id => now - (fetchedAtById.get(id) ?? 0) >= TWO_MINUTES_MS,
			)

			if (newPlotIds.length > 0) {
				await fetchPlots(newPlotIds)
			}
		},
		[fetchedAtById, fetchPlots, plotFetcher.state],
	)

	useEffect(() => {
		if (plotFetcher.state !== 'idle') return
		requestInFlight.current = false
		if (!pendingCenter.current) return
		const timer = setTimeout(() => {
			const center = pendingCenter.current
			pendingCenter.current = null
			if (center) void reconcileVisiblePlotIds(center)
		}, 0)
		return () => clearTimeout(timer)
	}, [plotFetcher.state, plotFetcher.data, reconcileVisiblePlotIds])

	const reconcileSmallClusterPlotIds = useCallback(
		(plotIds: string[]) => {
			addSmallClusterPlotIds(plotIds)
		},
		[addSmallClusterPlotIds],
	)

	const reconcileMediumClusterPlotIds = useCallback(
		(plotIds: string[]) => {
			addMediumClusterPlotIds(plotIds)
		},
		[addMediumClusterPlotIds],
	)

	useEffect(() => {
		if (!plotFetcher.data) return

		const { plots, requestedPlotIds } = plotFetcher.data
		if (plots) {
			addLoadedPlots(plots)
		}
		if (requestedPlotIds) {
			const newlyFetched = requestedPlotIds.map(plotId => ({
				plotId,
				fetchedAt: Date.now(),
			}))
			setFetchedPlotIds(newlyFetched)
		}
	}, [plotFetcher.data, addLoadedPlots, setFetchedPlotIds])

	useEffect(() => {
		if (!fetchedSmallClusterPlotIds) return

		reconcileSmallClusterPlotIds(fetchedSmallClusterPlotIds)
	}, [fetchedSmallClusterPlotIds, reconcileSmallClusterPlotIds])

	useEffect(() => {
		if (!fetchedMediumClusterPlotIds) return

		reconcileMediumClusterPlotIds(fetchedMediumClusterPlotIds)
	}, [fetchedMediumClusterPlotIds, reconcileMediumClusterPlotIds])

	// Store globally fetched clusters
	useEffect(() => {
		const fetchedMedium = largeClusterFetcher.data?.mediumClusterPlotIds
		if (fetchedMedium && fetchedMedium.length) {
			addMediumClusterPlotIds(fetchedMedium)
		}
	}, [largeClusterFetcher.data?.mediumClusterPlotIds, addMediumClusterPlotIds])

	useEffect(() => {
		const fetchedSmall = largeClusterFetcher.data?.smallClusterPlotIds
		if (fetchedSmall && fetchedSmall.length) {
			addSmallClusterPlotIds(fetchedSmall)
		}
	}, [largeClusterFetcher.data?.smallClusterPlotIds, addSmallClusterPlotIds])

	useEffect(() => {
		if (!fetchedLargeClusterPlotIds || !fetchedLargeClusterPlotIds.length)
			return
		setLargeClusterPlotIds(fetchedLargeClusterPlotIds)
	}, [fetchedLargeClusterPlotIds, setLargeClusterPlotIds])

	useEffect(() => {
		if (!fetchedLargestClusterPlotIds || !fetchedLargestClusterPlotIds.length)
			return
		setLargestClusterPlotIds(fetchedLargestClusterPlotIds)
	}, [fetchedLargestClusterPlotIds, setLargestClusterPlotIds])

	return reconcileVisiblePlotIds
}
